import {
  BlockScroll,
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { addDays, startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useState } from "react";
import { Button } from "@/components/ui/button/button";
import {
  Calendar,
  type CalendarProps,
} from "@/components/ui/calendar/calendar";
import { Typography } from "@/components/ui/typography";
import useTimezone from "@/hooks/use-timezone";
import { isoStringToZonedDate } from "@/libs/date-fns";

interface DueDateCalendarProps {
  dueDate: {
    key: string;
    value: string;
  };
  className?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (date: any) => void;
  calendarDisabled?: CalendarProps["disabled"];
}

export function DueDateCalendar({
  open,
  onOpenChange,
  dueDate,
  onChange,
  calendarDisabled,
}: DueDateCalendarProps) {
  const { timeZone } = useTimezone();
  const disabled = calendarDisabled ?? {
    before: startOfDay(addDays(toZonedTime(new Date(), timeZone), 1)),
  };
  const [date, setDate] = useState<Date | undefined>(
    isoStringToZonedDate(dueDate?.value, timeZone),
  );

  const onSave = () => {
    if (date) {
      onChange({
        key: "CUSTOM",
        value: date.toISOString(),
      });
      onOpenChange(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="flex flex-col w-96 gap-0 p-0">
        <ModalHeader className="text-left">
          <ModalTitle className="p-0">Set a date</ModalTitle>
          <ModalDescription className="sr-only">Set a date</ModalDescription>
        </ModalHeader>
        <BlockScroll>
          <div className="p-4 pt-1 flex flex-col gap-6">
            <Typography className="text-text-dark-high">
              Choose a deadline by which you'd prefer to have this order
              completed.
            </Typography>
            <div className="flex items-center justify-center text-center">
              <Calendar
                data-test="calendar"
                mode="single"
                selected={date}
                defaultMonth={date}
                onSelect={setDate}
                timeZone={timeZone}
                className="w-75"
                disabled={disabled}
                fixedWeeks
              />
            </div>
          </div>
        </BlockScroll>
        <ModalFooter>
          <ModalClose asChild>
            <Button type="button" kind="outline" variant="neutral">
              Cancel
            </Button>
          </ModalClose>
          <Button
            kind="solid"
            variant="primary"
            onClick={onSave}
            disabled={!date}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
